<template>
  <div>
    <el-dialog width="calc(100% - 48px)" top="24px" append-to-body custom-class="consultation-overview-dialog"
               :close-on-press-escape="false" :close-on-click-modal="false"
               :visible.sync="dialogVisible" :before-close="handleClose">
      <template slot="title">
        <h2 class="fs-24 font-bold">{{ $t(isUpdate ? 'Cập nhật Phiếu khám bệnh' : 'Viết Phiếu khám bệnh') }}</h2>
      </template>

      <b-overlay class="consultation-overlay" :show="loading || rendering" spinner-variant="primary" spinner-type="grow" spinner-small rounded="sm">
        <div class="consultation-main" :class="{ 'has-quick-results': quickResultsVisible }">
          <div class="consultation-form-pane">
            <div v-if="templateSections.length" class="consultation-form-container">
              <template v-for="section in templateSections">
                <section :key="section.title" class="mb-4">
                  <div class="section-heading">
                    <h3 class="section-title mb-0">{{ section.title }}</h3>
                    <el-button v-if="isParaclinicalSection(section) && !consultationData.sign" size="mini" plain type="warning"
                               icon="el-icon-warning-outline" :loading="addingAbnormalInfo" @click="appendAbnormalInformation(section)">
                      Thêm thông tin bất thường
                    </el-button>
                  </div>
              <b-row>
                <b-col v-for="field in section.fields" :key="field.key" :cols="field.type === 'textarea' ? 12 : 6">
                  <b-form-group :label="field.label" :label-for="field.key">
                    <el-input :id="field.key" v-model="formValues[field.key]"
                              :type="field.type === 'textarea' ? 'textarea' : 'text'"
                              :rows="field.type === 'textarea' ? 3 : undefined"
                              :disabled="!!consultationData.sign" :placeholder="`Nhập ${field.label.toLowerCase()}`"
                              @input="handleFieldInput(field.key)" />
                  </b-form-group>
                </b-col>
              </b-row>
                </section>

                <section v-if="section.title === 'VII. Chẩn đoán'" :key="`${section.title}-icd`" class="mb-4">
              <div class="section-heading diagnosis-heading">
                <h3 class="section-title mb-0">Chẩn đoán ICD</h3>
                <el-button v-if="!consultationData.sign" type="primary" icon="el-icon-plus" circle
                           title="Thêm ICD" aria-label="Thêm ICD" @click="addIcd" />
              </div>
              <div class="diagnosis-group">
                <div class="diagnosis-label">Chẩn đoán ban đầu</div>
                <div class="icd-row">
                  <SelectDiseaseICD10 :dataIcd="initialIcd" :disabled="!!consultationData.sign" @onChange="updateInitialIcd" />
                </div>
              </div>
              <div class="diagnosis-group">
                <div class="diagnosis-label">Chẩn đoán xác định</div>
                <div v-for="(icd, index) in icds" :key="icd.uid" class="icd-row">
                  <SelectDiseaseICD10 :dataIcd="icd" :disabled="!!consultationData.sign" @onChange="updateIcd(index, $event)" />
                  <el-button v-if="!consultationData.sign && icds.length > 1" type="danger" icon="el-icon-delete"
                             circle title="Xóa ICD" aria-label="Xóa ICD" @click="removeIcd(index)" />
                </div>
              </div>
                </section>
              </template>
            </div>
            <el-empty v-else description="Đang tải cấu hình phiếu khám" />
          </div>

          <aside v-if="quickResultsVisible" class="quick-results-panel">
            <div class="quick-results-panel__header">
              <div><p class="eyebrow">CHỈ ĐỊNH</p><h3>Kết quả nhanh</h3></div>
              <div class="quick-results-panel__actions">
                <el-button v-if="hasSelectedQuickResultsInCurrentCategory" type="text" class="quick-results-clear" @click="clearQuickResultSelections">
                  Bỏ chọn tất cả
                </el-button>
                <el-button type="text" icon="el-icon-close" aria-label="Đóng kết quả nhanh" @click="quickResultsVisible = false" />
              </div>
            </div>
            <div class="quick-results-tabs">
              <button v-for="tab in quickResultTabs" :key="tab.id" :class="{ active: quickResultCategory === tab.id }" @click="quickResultCategory = tab.id">
                {{ tab.name }} <b>{{ tab.count }}</b>
              </button>
            </div>
            <div v-loading="loadingQuickResults" class="quick-results-panel__body">
              <template v-for="item in filteredQuickResultItems">
                <div v-if="item.isGroup" :key="item.key" class="quick-result-group">{{ item.name }}</div>
                <div v-else :key="item.key" class="quick-result-item" :class="{ 'is-abnormal': item.isAbnormal, selected: selectedQuickResultKeys.includes(item.key), disabled: !canSelectQuickResult(item) }"
                     :role="canSelectQuickResult(item) ? 'checkbox' : undefined" :aria-checked="selectedQuickResultKeys.includes(item.key)" :tabindex="canSelectQuickResult(item) ? 0 : -1"
                     @click="toggleQuickResultSelection(item)" @keydown.enter.prevent="toggleQuickResultSelection(item)">
                  <div class="quick-result-item__title">
                    <el-checkbox :value="selectedQuickResultKeys.includes(item.key)" :disabled="!canSelectQuickResult(item)" />
                    <strong>{{ item.name }}</strong>
                  </div>
                  <template v-if="item.kind === 'lab'">
                    <span>Kết quả: <b>{{ item.value || '—' }}</b>{{ item.alert ? ` (${item.alert})` : '' }}{{ item.unit ? ` ${item.unit}` : '' }}</span>
                    <small>Tham chiếu: {{ item.referenceRange || '—' }}</small>
                  </template>
                  <template v-else>
                    <span>{{ item.conclusion || 'Chưa có kết quả chi tiết' }}</span>
                  </template>
                </div>
              </template>
              <el-empty v-if="!loadingQuickResults && !filteredQuickResultItems.length" description="Chưa có kết quả chỉ định" :image-size="70" />
            </div>
          </aside>
        </div>
      </b-overlay>

      <template slot="footer">
        <div v-if="patientVisit" class="consultation-modal-footer">
          <button :disabled="!!consultationData.sign || loading || submitting || rendering || !templateSections.length" type="button"
                  class="btn bg-pri bd-pri text-white upload-btn2 radius-10 w-40" @click="modifyConsultation">
            Lưu
          </button>
          <button v-if="isUpdate && consultationData.id && consultationData.document_link" type="button"
                  class="btn bg-pri bd-pri text-white upload-btn2 radius-10 w-40" @click="handlePrint">
            Xem / In phiếu
          </button>
          <button type="button" class="btn btn-light radius-10" @click="toggleQuickResults" :disabled="loading || rendering">
            <span>{{ quickResultsVisible ? 'Ẩn kết quả nhanh' : 'Xem nhanh kết quả' }}</span>
          </button>
          <button type="button" class="btn btn-light radius-10" @click="handleClose" :disabled="loading || rendering">
            Đóng
          </button>
        </div>
      </template>
    </el-dialog>

    <el-dialog fullscreen :visible.sync="pdfPreviewVisible" :before-close="handleClosePdfPreview">
      <div class="pdf-preview">
        <iframe v-if="previewUrl" ref="previewFrame" class="pdf-frame" :src="previewUrl"
                title="Xem trước PDF" @load="onPreviewLoaded" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { BCol, BFormGroup, BOverlay, BRow } from 'bootstrap-vue'
import appUtils from '@/utils/appUtils'
import { mixinGetPersonInfo } from '@/utils/mixinGetPersonInfo'
import { TEMPLATE_FORMS } from '@/constants/templateConstant'
import SelectDiseaseICD10 from '@/components/SelectDiseaseICD10/index.vue'
import { getCachedIndicationResult, getIndicationDetailCacheKey, getCachedLisResultData } from '@/utils/indicationResultsCache'

export default {
  name: 'ModalViewConsultationV2',
  mixins: [mixinGetPersonInfo],
  components: { BCol, BFormGroup, BOverlay, BRow, SelectDiseaseICD10 },
  props: {
    vitalSigns: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      dialogVisible: false,
      pdfPreviewVisible: false,
      loading: false,
      submitting: false,
      rendering: false,
      quickResultsVisible: false,
      loadingQuickResults: false,
      addingAbnormalInfo: false,
      quickResultItems: [],
      quickResultCategory: 1,
      selectedQuickResultKeys: [],
      quickResultsPromise: null,
      patientVisit: null,
      consultationData: {},
      formValues: {},
      initialIcd: { uid: 'initial', code: '', name: '' },
      icds: [],
      consultation_id: null,
      patientVisitId: 0,
      isUpdate: false,
      previewUrl: '',
      pendingPrint: false,
      template: null,
      templatePromise: null,
      templateID: TEMPLATE_FORMS.MAU_PHIEU_KHAM_BENH_NOI_KHOA
    }
  },
  computed: {
    allTemplateSections () {
      return this.template?.fields?.sections || []
    },
    templateSections () {
      return this.allTemplateSections.filter(section => !['I. Thông tin khách hàng', 'Thông tin khác', 'Sinh hiệu'].includes(section.title))
    },
    quickResultTabs () {
      const tabs = [
        { id: 1, name: 'Xét nghiệm' },
        { id: 2, name: 'CĐHA' },
        { id: 4, name: 'Sinh học phân tử' },
        { id: 3, name: 'Chỉ định khác' }
      ]
      return tabs.map(tab => ({ ...tab, count: this.quickResultItems.filter(item => !item.isGroup && item.cate === tab.id).length }))
    },
    filteredQuickResultItems () {
      return this.quickResultItems.filter(item => item.cate === this.quickResultCategory)
    },
    hasSelectedQuickResultsInCurrentCategory () {
      return this.filteredQuickResultItems.some(item => !item.isGroup && this.selectedQuickResultKeys.includes(item.key))
    }
  },
  async created () {
    await this.loadTemplate().catch(error => console.log(error))
  },
  methods: {
    async loadTemplate () {
      if (this.template?.id) return this.template
      if (!this.templatePromise) {
        this.templatePromise = await this.$rf.getRequest('CommonRequest').getTemplates(this.templateID)
          .then(response => {
            this.template = response?.data || null
            return this.template
          })
          .finally(() => {
            this.templatePromise = null
          })
      }
      return this.templatePromise
    },
    getGender (gender) {
      return gender === 1
        ? this.$t('schedule_page.lbl_male')
        : gender === 2
          ? this.$t('schedule_page.lbl_female')
          : gender === 3 ? this.$t('schedule_page.lbl_other') : ''
    },
    getTemplateValues () {
      return this.templateSections.reduce((values, section) => {
        section.fields.forEach(field => { values[field.key] = '' })
        return values
      }, {})
    },
    getPersonValues (patientVisit) {
      const person = patientVisit?.person || {}
      return {
        patient_name: person.name || '',
        patient_code: person.pid || person.code || '',
        dob: person.birthday ? window.moment(person.birthday).format('DD/MM/YYYY') : '',
        gender: this.getGender(person.gender),
        occupation: person.career || person.occupation || '',
        ethnicity: person.ethnic_name || person.ethnicity || '',
        nationality: person.nationality_name || person.nationality || '',
        phone: person.phone || '',
        workplace: person.workplace || '',
        address: this.getAddress(person),
        emergency_contact: person.emergency_contact || '',
        registration_time: patientVisit?.start_time ? window.moment(patientVisit.start_time).format('HH:mm DD/MM/YYYY') : '',
        chief_complaint: patientVisit?.visit_reason || ''
      }
    },
    getAutoValues (patientVisit) {
      const now = window.moment()
      return {
        ...this.getPersonValues(patientVisit),
        date: now.format('DD'),
        month: now.format('MM'),
        year: now.format('YYYY'),
        doctor_name: this.$user?.name || ''
      }
    },
    initializeForm (patientVisit, values = {}) {
      this.formValues = { ...this.getTemplateValues(), ...this.getAutoValues(patientVisit), ...values, ...this.getVitalSignsFromStorage() }
    },
    getVitalSignsStorageKey () {
      return `patient-visit-vitals:${this.patientVisit?.id || this.patientVisitId}`
    },
    getVitalSignsFromStorage () {
      return { ...(this.vitalSigns || {}) }
    },
    isParaclinicalSection (section) {
      return section?.title === 'VI. Cận lâm sàng'
    },
    getAllIndications () {
      return (this.patientVisit?.indication_order || []).reduce((items, order) => items.concat((order.indication || []).map(indication => ({
        ...indication,
        cate: Number(order.cate),
        orderId: order.id
      }))), [])
    },
    isAbnormalLaboratoryValue (value) {
      if (value?.is_ncs === 1 || value?.is_alert) return true
      if (value?.value === '' || value?.value === null || value?.lower === '' || value?.lower === null || value?.upper === '' || value?.upper === null) return false
      const result = Number(value?.value)
      const lower = Number(value?.lower)
      const upper = Number(value?.upper)
      return !Number.isNaN(result) && !Number.isNaN(lower) && !Number.isNaN(upper) && lower !== upper && (result < lower || result > upper)
    },
    getReferenceRange (value) {
      return value?.normal_rate || (value?.lower || value?.upper ? `${value.lower || '—'} - ${value.upper || '—'}` : '')
    },
    getLaboratoryAlert (value) {
      if (!this.isAbnormalLaboratoryValue(value)) return ''
      const result = Number(value?.value)
      const lower = Number(value?.lower)
      const upper = Number(value?.upper)
      if (!Number.isNaN(result) && !Number.isNaN(lower) && !Number.isNaN(upper)) return result < lower ? 'L' : result > upper ? 'H' : ''
      return 'Bất thường'
    },
    getConclusion (detail) {
      const conclusion = detail?.examination_results?.conclusion?.conclusion || detail?.examination_results?.conclusion?.description || ''
      return conclusion ? appUtils.cleanHtml(conclusion).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : ''
    },
    async getLaboratoryResultGroups (indications) {
      const indicationIds = indications.map(item => item.id).filter(Boolean)
      if (!indicationIds.length) return []
      const response = await getCachedLisResultData(indicationIds, () => this.$rf.getRequest('DoctorRequest').getLisIndicationsResultData({ indication_ids: indicationIds.join(',') }))
      return response?.data?.Data || []
    },
    async getIndicationResultDetail (indicationId) {
      return getCachedIndicationResult(
        getIndicationDetailCacheKey(indicationId),
        () => this.$rf.getRequest('DoctorRequest').getIndicationById(indicationId).then(response => response?.data)
      )
    },
    async toggleQuickResults () {
      this.quickResultsVisible = !this.quickResultsVisible
      if (this.quickResultsVisible) {
        this.quickResultCategory = 1
        if (!this.quickResultItems.length && !this.loadingQuickResults) this.loadQuickResults()
      }
    },
    async loadQuickResults () {
      if (!this.patientVisit?.id || !this.$rf) return []
      if (this.quickResultsPromise) return this.quickResultsPromise
      const indications = this.getAllIndications()
      const laboratoryIndications = indications.filter(item => item.cate === 1)
      const otherIndications = indications.filter(item => item.cate !== 1)
      this.quickResultsPromise = (async () => {
        try {
          this.loadingQuickResults = true
          const [laboratoryGroups, details] = await Promise.all([
            this.getLaboratoryResultGroups(laboratoryIndications),
            Promise.all(otherIndications.map(async indication => ({
              indication,
              detail: await this.getIndicationResultDetail(indication.id).catch(() => null)
            })))
          ])
          const laboratoryItems = laboratoryGroups.reduce((items, group, index) => {
            const values = group.examination_result_values || []
            if (values.length > 1) items.push({ key: `lab-group-${group.id || index}`, name: group.name || 'Xét nghiệm', cate: 1, isGroup: true })
            return items.concat(values.map(value => ({
              key: `lab-${value.id || `${group.id}-${value.name}`}`,
              kind: 'lab',
              cate: 1,
              indicationName: group.name || 'Xét nghiệm',
              name: value.name || group.name || '',
              value: value.value || '',
              unit: value.unit || '',
              referenceRange: this.getReferenceRange(value),
              isAbnormal: this.isAbnormalLaboratoryValue(value),
              alert: this.getLaboratoryAlert(value)
            })))
          }, [])
          const otherItems = details.map(({ indication, detail }) => ({
            key: `result-${indication.id}`,
            kind: 'other',
            cate: indication.cate,
            name: indication.name || '',
            conclusion: this.getConclusion(detail)
          }))
          this.quickResultItems = [...laboratoryItems, ...otherItems]
          return this.quickResultItems
        } catch (error) {
          this.quickResultItems = []
          this.$message({ message: this.$t('Không thể tải kết quả chỉ định'), type: 'warning' })
          return []
        } finally {
          this.loadingQuickResults = false
        }
      })()
      try {
        return await this.quickResultsPromise
      } finally {
        this.quickResultsPromise = null
      }
    },
    getParaclinicalField () {
      const section = this.templateSections.find(item => this.isParaclinicalSection(item))
      return section?.fields?.find(item => item.type === 'textarea') || section?.fields?.[0] || null
    },
    canSelectQuickResult (item) {
      return item.kind === 'lab' ? item.isAbnormal : Boolean(item.conclusion)
    },
    getQuickResultContent (item) {
      return item.kind === 'lab'
        ? `- ${item.indicationName || 'Xét nghiệm'}: ${item.name}: ${item.value || '—'}${item.alert ? ` (${item.alert})` : ''}${item.unit ? ` ${item.unit}` : ''}${item.referenceRange ? ` (BT: ${item.referenceRange})` : ''}`
        : `- ${item.name}${item.conclusion ? `: ${item.conclusion}` : ''}`
    },
    toggleQuickResultSelection (item) {
      if (!this.canSelectQuickResult(item)) return
      const selected = !this.selectedQuickResultKeys.includes(item.key)
      if (selected) {
        this.selectedQuickResultKeys.push(item.key)
        this.appendQuickResultToParaclinical(item)
        return
      }
      this.selectedQuickResultKeys = this.selectedQuickResultKeys.filter(key => key !== item.key)
      this.removeQuickResultFromParaclinical(item)
    },
    clearQuickResultSelections () {
      const selectedItems = this.filteredQuickResultItems.filter(item => !item.isGroup && this.selectedQuickResultKeys.includes(item.key))
      selectedItems.forEach(item => this.removeQuickResultFromParaclinical(item))
      const selectedKeys = selectedItems.map(item => item.key)
      this.selectedQuickResultKeys = this.selectedQuickResultKeys.filter(key => !selectedKeys.includes(key))
    },
    appendQuickResultToParaclinical (item) {
      const field = this.getParaclinicalField()
      if (!field) return
      const content = this.getQuickResultContent(item)
      const currentValue = String(this.formValues[field.key] || '').trim()
      if (currentValue.includes(content)) return
      this.$set(this.formValues, field.key, [currentValue, content].filter(Boolean).join('\n'))
    },
    removeQuickResultFromParaclinical (item) {
      const field = this.getParaclinicalField()
      if (!field) return
      const content = this.getQuickResultContent(item)
      const value = String(this.formValues[field.key] || '')
      this.$set(this.formValues, field.key, value.split(content).join('').replace(/\n{3,}/g, '\n\n').trim())
    },
    async appendAbnormalInformation (section) {
      if (!this.patientVisit?.id || !this.$rf) return
      const field = section.fields.find(item => item.type === 'textarea') || section.fields[0]
      if (!field) return
      const indications = this.getAllIndications()
      const laboratoryIndications = indications.filter(item => item.cate === 1)
      const imagingIndications = indications.filter(item => item.cate === 2)
      try {
        this.addingAbnormalInfo = true
        const [laboratoryGroups, imagingDetails] = await Promise.all([
          this.getLaboratoryResultGroups(laboratoryIndications),
          Promise.all(imagingIndications.map(async indication => ({
            indication,
            detail: await this.getIndicationResultDetail(indication.id).catch(() => null)
          })))
        ])
        const abnormalLines = laboratoryGroups.reduce((lines, group) => {
          const values = (group.examination_result_values || []).filter(value => this.isAbnormalLaboratoryValue(value))
          return lines.concat(values.map(value => this.getQuickResultContent({
            kind: 'lab',
            indicationName: group.name || 'Xét nghiệm',
            name: value.name || group.name || '',
            value: value.value || '',
            unit: value.unit || '',
            referenceRange: this.getReferenceRange(value),
            alert: this.getLaboratoryAlert(value)
          })))
        }, [])
        const imagingLines = imagingDetails
          .map(({ indication, detail }) => ({ name: indication.name || '', conclusion: this.getConclusion(detail) }))
          .filter(item => item.name && item.conclusion)
          .map(item => `- ${item.name}: ${item.conclusion}`)
        const content = [
          abnormalLines.length ? `Xét nghiệm bất thường:\n${abnormalLines.join('\n')}` : '',
          imagingLines.length ? `Chẩn đoán hình ảnh:\n${imagingLines.join('\n')}` : ''
        ].filter(Boolean).join('\n\n')
        if (!content) {
          this.$message({ message: this.$t('Chưa có thông tin bất thường hoặc kết luận chẩn đoán hình ảnh'), type: 'info' })
          return
        }
        const currentValue = String(this.formValues[field.key] || '').trim()
        if (!currentValue.includes(content)) this.$set(this.formValues, field.key, [currentValue, content].filter(Boolean).join('\n\n'))
        await this.loadQuickResults()
        const addedKeys = this.quickResultItems
          .filter(item => (item.kind === 'lab' && item.isAbnormal) || (item.cate === 2 && item.conclusion))
          .map(item => item.key)
        this.selectedQuickResultKeys = Array.from(new Set([...this.selectedQuickResultKeys, ...addedKeys]))
      } catch (error) {
        this.$message({ message: this.$t('Không thể thêm thông tin cận lâm sàng'), type: 'error' })
      } finally {
        this.addingAbnormalInfo = false
      }
    },
    addIcd () {
      this.icds.push(this.createIcd())
    },
    createIcd (data = {}) {
      return {
        uid: `${Date.now()}-${Math.random()}`,
        code: data.code || '',
        name: data.name || ''
      }
    },
    updateInitialIcd (data) {
      this.initialIcd = { ...this.initialIcd, code: data?.code || '', name: data?.name || '' }
      this.syncDiagnosisField()
    },
    updateIcd (index, data) {
      this.$set(this.icds, index, { ...this.icds[index], code: data?.code || '', name: data?.name || '' })
      this.syncDiagnosisField()
    },
    removeIcd (index) {
      this.icds.splice(index, 1)
      this.syncDiagnosisField()
    },
    handleFieldInput (key) {
      if (key === 'diagnosis') this.syncIcdsFromDiagnosis()
    },
    formatIcd (icd) {
      return `ICD: ${icd.code}${icd.code && icd.name ? ' - ' : ''}${icd.name};`
    },
    getFreeDiagnosisText (diagnosis) {
      return String(diagnosis || '')
        .replace(/Chẩn đoán (?:BD|ban đầu):\s*(?:ICD:\s*)?[^;\n]*;?/gi, '')
        .replace(/Chẩn đoán xác định:\s*/gi, '')
        .replace(/ICD:\s*[^;\n]+;?/gi, '')
        .replace(/^[;\s]+|[;\s]+$/g, '')
        .trim()
    },
    syncDiagnosisField () {
      const diagnosis = this.getFreeDiagnosisText(this.formValues.diagnosis)
      const initialLine = this.initialIcd.code || this.initialIcd.name
        ? `Chẩn đoán BD: ${this.formatIcd(this.initialIcd)}`
        : ''
      const definitive = this.icds.filter(item => item.code || item.name)
      const definitiveLines = definitive.map(item => this.formatIcd(item))
      const definitiveBlock = definitiveLines.length
        ? ['Chẩn đoán xác định:', ...definitiveLines].join('\n')
        : ''
      this.$set(this.formValues, 'diagnosis', [initialLine, definitiveBlock, diagnosis].filter(Boolean).join('\n'))
    },
    syncIcdsFromDiagnosis () {
      const diagnosis = String(this.formValues.diagnosis || '')
      const parsed = this.parseDiagnosisIcd(diagnosis)
      this.initialIcd = parsed.initial || this.createIcd()
      this.icds = parsed.definitive
      if (!this.icds.length) this.addIcd()
    },
    encodeContent () {
      return appUtils.utf8ToBase64(JSON.stringify({ values: { ...this.getAutoValues(this.patientVisit), ...this.formValues, ...this.getVitalSignsFromStorage(), icd: this.getIcdText() } }))
    },
    decodeContent (content) {
      if (!content) return null
      try {
        const decoded = appUtils.base64ToUtf8(content)
        return JSON.parse(decoded)
      } catch (error) {
        try { return JSON.parse(content) } catch (parseError) { return null }
      }
    },
    hydrateContent (content) {
      const saved = this.decodeContent(content)
      this.initializeForm(this.patientVisit, saved?.values || {})
      const diagnosis = saved?.values?.diagnosis || ''
      const parsed = this.parseDiagnosisIcd(diagnosis)
      const savedIcd = saved?.values?.icd || ''
      this.initialIcd = parsed.initial || this.createIcd()
      this.icds = parsed.definitive.length ? parsed.definitive : this.parseIcdText(savedIcd)
      if (!this.icds.length) this.addIcd()
      this.syncDiagnosisField()
    },
    getIcdTextFromDiagnosis (diagnosis) {
      return Array.from(String(diagnosis || '').matchAll(/^ICD:\s*(.+)$/gim)).map(match => match[1]).join('; ')
    },
    parseIcdText (value) {
      return String(value || '').split(';').map(item => this.parseIcdEntry(item)).filter(item => item.code || item.name)
    },
    parseIcdEntry (value) {
      const text = String(value || '').replace(/^ICD:\s*/i, '').replace(/;\s*$/, '').trim()
      if (!text) return this.createIcd()
      const match = text.match(/^([A-Za-z0-9.]+)\s*-\s*(.+)$/)
      return this.createIcd({
        code: match?.[1] || '',
        name: match?.[2] || text
      })
    },
    parseDiagnosisIcd (diagnosis) {
      const source = String(diagnosis || '')
      const initialMatch = source.match(/Chẩn đoán (?:BD|ban đầu):\s*(?:ICD:\s*)?([^;\n]+);?/i)
      const initialEnd = initialMatch ? initialMatch.index + initialMatch[0].length : -1
      const definitive = Array.from(source.matchAll(/ICD:\s*([^;\n]+);?/gi))
        .filter(match => !initialMatch || match.index < initialMatch.index || match.index >= initialEnd)
        .map(match => this.parseIcdEntry(match[1]))
        .filter(item => item.code || item.name)
      return {
        initial: initialMatch ? this.parseIcdEntry(initialMatch[1]) : null,
        definitive
      }
    },
    async getPatientVisit (id) {
      await this.loadTemplate()
      this.loading = true
      try {
        const response = await this.$rf.getRequest('DoctorRequest').getDetailPatientVisit(id)
        this.patientVisit = response?.data || this.patientVisit
        const consultation = this.patientVisit?.array_consultation?.find(item => String(item.id) === String(this.consultation_id))
        if (!consultation) return
        this.consultationData = consultation
        this.hydrateContent(this.consultationData.content)
      } finally {
        this.loading = false
      }
    },
    async openDialog (patientVisit, consultationId) {
      this.patientVisit = patientVisit
      this.patientVisitId = patientVisit?.id
      this.consultation_id = consultationId || null
      this.isUpdate = !!consultationId
      this.consultationData = {}
      this.quickResultsVisible = false
      this.quickResultItems = []
      this.selectedQuickResultKeys = []
      this.quickResultCategory = 1
      this.initialIcd = this.createIcd()
      this.icds = []
      await this.loadTemplate()
      this.initializeForm(patientVisit)
      if (this.isUpdate) {
        await this.getPatientVisit(this.patientVisitId)
      } else {
        this.addIcd()
      }
      this.loadQuickResults()
      this.dialogVisible = true
    },
    handleClose () {
      this.dialogVisible = false
      this.consultationData = {}
      this.formValues = {}
      this.initialIcd = this.createIcd({ uid: 'initial' })
      this.icds = []
      this.consultation_id = null
      this.quickResultsVisible = false
      this.quickResultItems = []
      this.selectedQuickResultKeys = []
      this.quickResultCategory = 1
    },
    async modifyConsultation () {
      if (!this.patientVisit?.id) return
      this.loading = true
      this.submitting = true
      try {
        const payload = { patient_visit_id: this.patientVisit.id, content: this.encodeContent() }
        const request = this.$rf.getRequest('DoctorRequest')
        const response = this.isUpdate
          ? await request.updateConsultationV2(this.consultationData.id, payload)
          : await request.addConsulation(payload)
        const id = this.consultationData.id || response?.data?.id
        if (!id) throw new Error('Consultation id is missing')
        this.consultation_id = id
        this.isUpdate = true
        await this.getPatientVisit(this.patientVisit.id)
        await this.renderAndSaveDocument()
        this.openPreview(false)
        this.$emit('refresh')
        this.$message({ message: this.$t('Lưu thành công'), type: 'success' })
      } catch (error) {
        console.log(error)
        this.$message({ message: this.$t('Lưu thất bại'), type: 'error' })
      } finally {
        this.loading = false
        this.submitting = false
      }
    },
    buildRenderPayload () {
      const icdText = this.getIcdText()
      return {
        values: {
          ...this.getAutoValues(this.patientVisit),
          ...this.formValues,
          ...this.getVitalSignsFromStorage(),
          icd: icdText,
          diagnosis: this.formValues.diagnosis
        },
        repeats: {}
      }
    },
    getIcdText () {
      const icdText = [this.initialIcd, ...this.icds]
        .filter(item => item.code || item.name)
        .map(item => `${item.code}${item.code && item.name ? ' - ' : ''}${item.name}`)
        .join('; ')
      return icdText ? `${icdText};` : ''
    },
    async renderDocument () {
      if (!this.template?.id) throw new Error('Consultation template is missing')
      const response = await this.$rf.getRequest('CommonRequest').renderDocument(this.template.id, this.buildRenderPayload())
      const previewPath = response?.data?.preview_url
      if (!previewPath) throw new Error('Rendered document URL is missing')
      this.previewUrl = this.getPreviewUrl(previewPath)
      return response.data
    },
    getPreviewUrl (previewPath) {
      if (/^https?:\/\//i.test(previewPath)) return `${previewPath}?t=${Date.now()}`
      const endpoint = String(process.env.VUE_APP_PRINT_PDF_TEMPLATE_ENDPOINT || '').replace(/\/$/, '')
      const path = String(previewPath).startsWith('/') ? previewPath : `/${previewPath}`
      return `${endpoint}${path}?t=${Date.now()}`
    },
    async saveRenderedDocument () {
      if (!this.previewUrl || !this.consultationData?.id) throw new Error('Preview document URL is missing')
      await this.$rf.getRequest('DoctorRequest').updateConsultationV2(this.consultationData.id, { document_link: this.previewUrl })
      this.consultationData = { ...this.consultationData, document_link: this.previewUrl }
      return this.previewUrl
    },
    async renderAndSaveDocument () {
      try {
        this.rendering = true
        await this.renderDocument()
        await this.saveRenderedDocument()
      } catch (error) {
        console.log(error)
        this.$message({ message: this.$t('Không thể tạo phiếu khám'), type: 'error' })
      } finally {
        this.rendering = false
      }
    },
    handlePrint () {
      if (!this.consultationData?.document_link) {
        this.$message({ message: this.$t('Không có phiếu khám để in. Hãy lưu lại phiếu khám'), type: 'warning' })
        return
      }
      this.openPreview(false)
    },
    openPreview (print = false) {
      this.previewUrl = this.consultationData.document_link
      this.pendingPrint = print
      this.pdfPreviewVisible = true
    },
    onPreviewLoaded () {
      if (!this.pendingPrint || !this.$refs.previewFrame) return
      this.pendingPrint = false
      const previewOrigin = new URL(this.previewUrl, window.location.href).origin
      if (previewOrigin === window.location.origin) {
        this.$refs.previewFrame.contentWindow.focus()
        this.$refs.previewFrame.contentWindow.print()
        return
      }

      // Cross-origin PDF frames cannot expose contentWindow.print(). Open the
      // same PDF in a browser tab where the native PDF viewer owns printing.
      const printWindow = window.open(this.previewUrl, '_blank', 'noopener,noreferrer')
      if (!printWindow) {
        this.$message({ message: this.$t('Trình duyệt đã chặn cửa sổ in. Hãy cho phép popup để in phiếu.'), type: 'warning' })
      }
    },
    handleClosePdfPreview () {
      this.pdfPreviewVisible = false
      this.previewUrl = ''
    }
  }
}
</script>

<style lang="scss">
.consultation-overview-dialog {
  height: calc(100vh - 48px);
  margin-bottom: 24px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  .el-dialog__header { flex: 0 0 auto; padding: 20px 24px; border-bottom: 1px solid #e3e9f2; }
  .el-dialog__headerbtn { z-index: 2; top: 20px; right: 18px; font-size: 22px; }
  .el-dialog__body { min-height: 0; flex: 1; display: flex; padding: 0; overflow: hidden; background: #f7f9fc; }
  .el-dialog__footer { flex: 0 0 auto; padding: 14px 24px; border-top: 1px solid #e3e9f2; background: #fff; }
}
.consultation-overlay { width: 100%; height: 100%; min-height: 0; flex: 1; display: flex; overflow: hidden; }
.consultation-main { min-height: 0; flex: 1; display: grid; grid-template-columns: minmax(0, 1fr); overflow: hidden; }
.consultation-main.has-quick-results { grid-template-columns: minmax(0, 1fr) minmax(360px, 440px); }
.consultation-form-pane { min-width: 0; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 28px 24px 40px; }
.consultation-form-container { max-width: 960px; margin: 0 auto; padding: 28px 32px; background: #fff; border: 1px solid #dfe5ee; border-radius: 10px; }
.consultation-modal-footer { display: flex; align-items: center; justify-content: center; gap: 10px; }
.quick-results-panel { min-width: 0; min-height: 0; display: flex; flex-direction: column; border-left: 1px solid #e3e9f2; background: #fff; }
.quick-results-panel__header { display: flex; align-items: center; justify-content: space-between; min-height: 82px; padding: 16px 20px; border-bottom: 1px solid #e3e9f2; }
.quick-results-panel__header h3 { margin: 2px 0 0; color: #1b3b70; font-size: 19px; font-weight: 700; }
.quick-results-panel__actions { display: flex; align-items: center; gap: 4px; }.quick-results-clear { color: #c23939; font-size: 12px; }.quick-results-clear:hover { color: #a82525; }
.quick-results-tabs { flex: 0 0 auto; display: flex; flex-wrap: wrap; gap: 5px; padding: 10px 12px; border-bottom: 1px solid #e3e9f2; background: #f7f9fc; }
.quick-results-tabs button { border: 0; padding: 6px 8px; border-radius: 5px; color: #64748b; background: transparent; cursor: pointer; font-size: 11px; }
.quick-results-tabs button:hover, .quick-results-tabs button.active { color: #255fc9; background: #fff; box-shadow: 0 1px 3px rgba(20, 46, 89, .12); font-weight: 700; }.quick-results-tabs b { margin-left: 3px; font-size: 10px; }
.quick-results-panel__body { min-height: 0; flex: 1; overflow-y: auto; overscroll-behavior: contain; padding: 12px; }
.quick-result-group { padding: 10px 12px; color: #364d70; background: #f1f5fa; border-radius: 6px; font-size: 12px; font-weight: 700; text-transform: uppercase; }
.quick-result-item { display: flex; flex-direction: column; gap: 4px; padding: 12px; border-bottom: 1px solid #e6ebf2; color: #41536e; font-size: 13px; cursor: pointer; }
.quick-result-item:hover, .quick-result-item.selected { background: #f5f9ff; }.quick-result-item.selected { box-shadow: inset 3px 0 #2864d7; }.quick-result-item.disabled { color: #8b98aa; cursor: default; }.quick-result-item.disabled:hover { background: transparent; }
.quick-result-item__title { display: flex; align-items: flex-start; gap: 8px; }.quick-result-item__title .el-checkbox { line-height: 18px; pointer-events: none; }.quick-result-item__title .el-checkbox__label { display: none; }
.quick-result-item strong { color: #273f61; }.quick-result-item span { line-height: 1.45; }.quick-result-item small { color: #7f8da3; }.quick-result-item.is-abnormal strong, .quick-result-item.is-abnormal span b { color: #c23939; }
.eyebrow { margin: 0; color: #7d8ba1; font-size: 11px; font-weight: 700; letter-spacing: .06em; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.section-title { margin-bottom: 12px; color: #20419b; font-size: 18px; font-weight: 600; }
.diagnosis-group { margin-bottom: 12px; }
.diagnosis-label { margin-bottom: 6px; color: #20419b; font-weight: 600; }
.icd-row { display: flex; align-items: flex-end; gap: 12px; margin-bottom: 12px; }
.icd-row > :first-child { flex: 1; }
.pdf-preview { height: calc(100vh - 80px); min-height: 720px; background: #d9dde3; overflow: hidden; }
.pdf-frame { width: 100%; height: 100%; border: 0; background: #fff; }
@media (max-width: 900px) {
  .consultation-main.has-quick-results { grid-template-columns: minmax(0, 1fr) minmax(300px, 38vw); }
  .consultation-form-container { padding: 22px; }
}
</style>
